import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin } from 'lucide-react';

const Experience: React.FC = () => {
  const experiences = [
    {
      company: 'Rebelfoods Private Ltd.',
      location: 'Mumbai, India',
      position: 'Senior Principal Engineer',
      period: 'June 2022 – Present',
      previousPosition: 'Principal Engineer (Dec 2020 – May 2022)',
      description: 'Leading technology initiatives and managing cross-functional teams in the food delivery ecosystem.',
      achievements: [
        'Direct and mentor a cross-functional team of 20+ engineers, fostering innovation and technical excellence',
        'Architected and led system modernization initiatives, improving scalability by 300% and reducing latency by 40%',
        'Designed and implemented microservices architecture ensuring seamless backward compatibility',
        'Established comprehensive monitoring framework using DataDog, reducing production incidents by 60%',
        'Optimized API performance through load testing analysis and code optimization, achieving sub-200ms response times'
      ],
      applications: [
        {
          name: 'OMS (Order Management System)',
          description: 'Handles order acceptance and kitchen display system integration with Swiggy, Zomato channels'
        },
        {
          name: 'CMS (Catalogue Management System)',
          description: 'Manages menus, categories, products, stores and delivery polygons for multiple channels'
        },
        {
          name: 'SPARK (Inventory System)',
          description: 'Real-time inventory management tied to product orders and lifecycle management'
        }
      ]
    },
    {
      company: 'Indiabulls Consumer Finance Ltd.',
      location: 'Mumbai, India',
      position: 'Lead Software Engineer',
      period: 'June 2019 – November 2020',
      description: 'Led development team delivering critical financial services applications.',
      achievements: [
        'Led development team of 7 engineers delivering critical financial services applications',
        'Integrated multiple third-party platforms (Cleartrip, RedBus, Euronet) enhancing service portfolio',
        'Collaborated with C-suite executives and business stakeholders on strategic technology initiatives',
        'Delivered projects 15% ahead of schedule through effective resource planning and risk management'
      ]
    },
    {
      company: 'Intellect Design Arena Ltd.',
      location: 'Mumbai, India',
      position: 'Team Lead',
      period: 'May 2018 – May 2019',
      description: 'Successfully delivered enterprise projects and established development frameworks.',
      achievements: [
        'Successfully delivered 2 enterprise projects managing a team of 4 developers',
        'Established software development framework and coding standards adopted company-wide',
        'Mentored junior developers in requirement analysis and technical implementation',
        'Maintained 100% project delivery success rate with zero critical post-deployment issues'
      ]
    },
    {
      company: 'Oracle India Pvt. Ltd.',
      location: 'Mumbai, India',
      position: 'Senior Applications Engineer',
      period: 'December 2016 – April 2018',
      previousPosition: 'Applications Engineer (Sep 2014 – Nov 2016)',
      description: 'Led architectural design initiatives and database performance optimization.',
      achievements: [
        'Led architectural design initiatives within agile development frameworks',
        'Spearheaded database performance optimization projects improving query performance by 50%',
        'Managed Oracle version migration for enterprise clients with zero downtime',
        'Maintained consistent delivery excellence with 98% on-time completion rate'
      ]
    },
    {
      company: 'Accenture India Pvt. Ltd.',
      location: 'Mumbai, India',
      position: 'Senior Software Engineer',
      period: 'September 2013 – August 2014',
      previousPositions: [
        'Software Engineer (Sep 2011 – Aug 2013)',
        'Associate Software Engineer (Jul 2010 – Aug 2011)'
      ],
      description: 'Achieved excellence in enterprise project delivery and system migrations.',
      achievements: [
        'Achieved 100% SLA compliance in first year with zero escalations',
        'Key contributor to Oracle-to-SAP migration affecting 50,000+ users',
        'Delivered 4 major enterprise projects with combined value of $2M+',
        'Recognized for technical excellence and promoted twice within 4 years'
      ]
    }
  ];

  return (
    <section id="experience" className="section-padding">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-6">
            Professional <span className="gradient-text">Experience</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            15+ years of progressive experience across diverse industries, leading teams and 
            delivering enterprise-scale solutions.
          </p>
        </motion.div>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary-500 to-secondary-500"></div>

                    <div className="space-y-12">
            {experiences.map((experience, index) => (
              <motion.div
                key={experience.company}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className={`relative flex items-start ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                                 {/* Timeline Dot with Year */}
                 <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 w-4 h-4 bg-primary-500 rounded-full border-4 border-white dark:border-dark-900 z-10"></div>
                 <div className="absolute left-12 md:left-1/2 transform md:-translate-x-1/2 md:translate-x-8 top-0 md:top-2 bg-primary-600 text-white text-xs font-bold px-2 py-1 rounded whitespace-nowrap z-10">
                   {index === 0 ? 'Ongoing' : 
                    index === 1 ? 'November 2020' :
                    index === 2 ? 'May 2019' :
                    index === 3 ? 'April 2018' :
                    index === 4 ? 'September 2014' :
                    index === 5 ? 'July 2010' : ''}
                 </div>

                {/* Content Card */}
                <div className={`ml-12 md:ml-0 md:w-5/12 ${
                  index % 2 === 0 ? 'md:mr-auto md:pr-8' : 'md:ml-auto md:pl-8'
                }`}>
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      className="card p-6"
                    >
                    {/* Company Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                          {experience.company}
                        </h3>
                        <p className="text-lg font-semibold text-primary-600 dark:text-primary-400 mb-2">
                          {experience.position}
                        </p>
                      </div>
                    </div>

                    {/* Location and Period */}
                    <div className="flex items-center space-x-4 mb-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center space-x-1">
                        <MapPin size={14} />
                        <span>{experience.location}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar size={14} />
                        <span>{experience.period}</span>
                      </div>
                    </div>

                    {/* Previous Positions */}
                    {experience.previousPosition && (
                      <div className="mb-4 p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          <span className="font-medium">Previously:</span> {experience.previousPosition}
                        </p>
                      </div>
                    )}

                    {experience.previousPositions && (
                      <div className="mb-4 p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          <span className="font-medium">Previous Roles:</span>
                        </p>
                        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                          {experience.previousPositions.map((position, posIndex) => (
                            <li key={posIndex} className="flex items-center">
                              <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                              {position}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Description */}
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      {experience.description}
                    </p>

                    {/* Key Achievements */}
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
                        Key Achievements:
                      </h4>
                      <ul className="space-y-2">
                        {experience.achievements.map((achievement, achIndex) => (
                          <li key={achIndex} className="flex items-start text-sm text-gray-600 dark:text-gray-400">
                            <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2 mt-2 flex-shrink-0"></div>
                            {achievement}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Applications (for Rebelfoods) */}
                    {experience.applications && (
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
                          Applications Worked On:
                        </h4>
                        <div className="space-y-3">
                          {experience.applications.map((app, appIndex) => (
                            <div key={appIndex} className="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                              <h5 className="font-medium text-primary-700 dark:text-primary-300 mb-1">
                                {app.name}
                              </h5>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {app.description}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                                         )}
                   </motion.div>
                </div>
              </motion.div>
            ))}
            
            {/* July 2010 Career Beginning Point */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.0 }}
              viewport={{ once: true }}
              className="relative flex items-start md:flex-row-reverse"
            >
              {/* Timeline Dot */}
              <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 w-4 h-4 bg-primary-500 rounded-full border-4 border-white dark:border-dark-900 z-10"></div>
              <div className="absolute left-12 md:left-1/2 transform md:-translate-x-1/2 md:translate-x-8 top-0 md:top-2 bg-primary-600 text-white text-xs font-bold px-2 py-1 rounded whitespace-nowrap z-10">
                July 2010
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
